import { createGameAssets } from "../game-assets.js";
import { blockedCells } from "../engine.js";
import { HINTS_PER_ROUND } from "../game-session.js";
import { REGION_COLORS } from "./palette.js";
import { brushCrossSvg } from "../animation/brush-cross.js";
import { TileInput } from "./tile-input.js";
import { MarkStroke } from "./mark-stroke.js";
import { LivesView } from "./lives-view.js";
import { TutorialView } from "./tutorial-view.js";
import {
  measureBoard,
  tileAtPoint,
  tilesAlongSegment,
} from "../board-geometry.js";

const icon = (name) =>
  `<svg class="icon" aria-hidden="true"><use href="#i-${name}"/></svg>`;
const time = (ms) =>
  `${String(Math.floor(ms / 60000)).padStart(2, "0")}:${String(Math.floor(ms / 1000) % 60).padStart(2, "0")}`;
const setText = (element, value) => {
  const text = String(value);
  if (element.textContent !== text) element.textContent = text;
};
const setAttribute = (element, name, value) => {
  const text = String(value);
  if (element.getAttribute(name) !== text) element.setAttribute(name, text);
};
const inputEvents = new Set(["pointerdown", "pointermove", "pointerup", "pointercancel", "lostpointercapture", "click", "contextmenu", "keydown", "change", "focusin"]);
const videoBadge = '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><rect x="1.5" y="3.5" width="17" height="13" rx="3" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="m8 6.5 5 3.5-5 3.5z" fill="currentColor"/></svg>';

/** Presentation/input adapter. State commands, animation clock and renderers are injected. */
export class GameView {
  constructor({
    assets = createGameAssets("/"),
    controller,
    renderer,
    fallbackRenderer,
    portraits,
    feedback,
    clock,
    beforeWonRoundExit,
    requestRewardedCatHint,
    root = document,
  }) {
    this.assets = assets;
    this.controller = controller;
    this.renderer = renderer;
    this.fallbackRenderer = fallbackRenderer;
    this.portraits = portraits;
    this.feedback = feedback;
    this.clock = clock;
    this.beforeWonRoundExit = beforeWonRoundExit;
    this.requestRewardedCatHint = requestRewardedCatHint;
    this.hostPaused = false;
    this.root = root;
    this.$ = (selector) => root.querySelector(selector);
    this.lives = new LivesView(this.$("#lives"));
    this.tutorialView = new TutorialView({
      root,
      onClose: () => {
        if (this.hostPaused || this.pendingHostAction) return;
        this.cancelPointer();
        this.controller.finishTutorial();
      },
      onNext: () => {
        if (this.hostPaused || this.pendingHostAction) return;
        this.cancelPointer();
        this.controller.nextTutorialStep();
      },
    });
    this.abort = new AbortController();
    this.offlineReady = false;
    this.input = new TileInput({
      perform: (command, index) => controller.perform(command, index),
      state: () => this.state,
      revision: () => controller.revision,
    });
    this.stroke = new MarkStroke({
      perform: (command, payload) => controller.perform(command, payload),
      state: () => this.state,
    });
  }
  async mount() {
    this.drawRuleDiagrams();
    this.bind();
    for (const [index, host] of [
      ...this.root.querySelectorAll("[data-cat-portrait]"),
    ].entries())
      this.portraits.register(host, 100 + index);
    const portraitReady = this.portraits
      .mount()
      .catch((error) =>
        console.warn("Character portrait initialization failed.", error),
      );
    if (this.controller.needsTutorial) this.controller.startTutorial();
    this.unsubscribe = this.controller.subscribe((state) => this.render(state));
    this.render(this.controller.snapshot());
    try {
      await this.renderer.mount(this.$("#board"));
    } catch (error) {
      console.warn("PixiJS initialization failed; using the DOM board.", error);
      try {
        this.renderer.destroy();
      } catch {}
      this.$("#board").classList.remove("pixi-ready");
      this.renderer = this.fallbackRenderer;
      await this.renderer.mount(this.$("#board"));
      this.render(this.controller.snapshot());
    }
    this.$("#board").classList.add("board-ready");
    await portraitReady;
    this.saveInterval = setInterval(() => {
      if (!document.hidden) this.controller.save();
    }, 5000);
    if (document.hidden) this.controller.pause();
  }
  listen(target, name, handler) {
    target.addEventListener(name, (event) => {
      if ((this.hostPaused || this.pendingHostAction) && inputEvents.has(name)) return;
      handler(event);
    }, { signal: this.abort.signal });
  }
  resumeIfReady() {
    if (!this.hostPaused && !this.pendingHostAction && !document.hidden && !this.root.querySelector("dialog[open]"))
      this.controller.resume();
  }
  freezeHostAnimations() {
    this.hostAnimations ??= new Set();
    for (const element of this.root.querySelectorAll("#game, dialog, #tutorial-overlay")) {
      for (const animation of element.getAnimations({ subtree: true })) {
        if (animation.playState !== "running") continue;
        this.hostAnimations.add(animation);
        animation.pause();
      }
    }
  }
  releaseHostAnimations() {
    for (const animation of this.hostAnimations || []) if (animation.playState === "paused") animation.play();
    this.hostAnimations?.clear();
  }
  setHostPaused(paused) {
    if (this.destroyed || this.hostPaused === paused) return;
    this.hostPaused = paused;
    for (const element of this.root.querySelectorAll("#game, dialog, #tutorial-overlay")) element.inert = paused;
    if (paused) this.$("#game").setAttribute("aria-busy", "true");
    else this.$("#game").removeAttribute("aria-busy");
    this.tutorialView.setHostPaused(paused);
    this.feedback.setHostPaused(paused);
    this.clock.setHostPaused(paused);
    if (paused) {
      this.cancelPointer();
      this.controller.pause();
      this.pausedDialogs = [...this.root.querySelectorAll("dialog[open]")];
      this.closeDialogs();
      this.freezeHostAnimations();
    } else {
      this.releaseHostAnimations();
      const dialogs = this.pausedDialogs || [];
      this.pausedDialogs = [];
      if (!this.pendingHostAction) for (const dialog of dialogs) if (dialog.isConnected && !dialog.open) dialog.showModal();
      this.completeHostAction();
      this.resumeIfReady();
      this.portraits.wake();
    }
  }
  prepareHostAction() {
    clearTimeout(this.resultTimer);
    this.pausedDialogs = [];
    this.cancelPointer();
    this.closeDialogs();
  }
  completeHostAction() {
    if (this.destroyed || this.hostPaused || document.hidden || !this.pendingHostAction?.settled) return;
    const { action } = this.pendingHostAction;
    this.pendingHostAction = null;
    this.pausedDialogs = [];
    action();
    this.closeDialogs();
    this.resumeIfReady();
  }
  exitRound(action, changesRound = true) {
    if (this.destroyed || this.hostPaused || this.pendingHostAction) return;
    const state = this.beforeWonRoundExit && changesRound ? this.controller.snapshot() : null;
    if (!state || state.status !== "won" || state.mode === "tutorial") {
      action();
      this.closeDialogs();
      return;
    }
    this.runHostAction(action, (context) => this.beforeWonRoundExit({ ...context, state }));
  }
  runHostAction(action, before) {
    if (this.destroyed || this.hostPaused || this.pendingHostAction) return;
    this.controller.save();
    this.pendingHostAction = { action, settled: false };
    const finish = () => {
      if (!this.pendingHostAction || this.destroyed) return;
      this.pendingHostAction.settled = true;
      this.completeHostAction();
    };
    try {
      const result = before({ prepare: () => this.prepareHostAction() });
      if (result && typeof result.then === "function") Promise.resolve(result).then(finish, finish);
      else finish();
    } catch (error) {
      console.warn("Host transition failed; continuing when the game is released.", error);
      finish();
    }
  }
  drawRuleDiagrams() {
    for (const diagram of this.root.querySelectorAll("[data-rule]")) {
      const type = diagram.dataset.rule,
        catIndex = type === "line" ? 1 : 4,
        crosses =
          type === "line"
            ? [0, 2, 4, 7]
            : type === "color"
              ? [0, 1, 2, 3, 6]
              : [0, 1, 2, 3, 5, 6, 7, 8];
      diagram.innerHTML = Array.from(
        { length: 9 },
        (_, index) =>
          `<span class="${crosses.includes(index) ? "cross" : ""}">${index === catIndex ? `<img src="${this.assets.cat128}" width="128" height="128" alt=""/>` : crosses.includes(index) ? "×" : ""}</span>`,
      ).join("");
    }
  }

  rebuildBoard(state, key) {
    this.releasePointer();
    this.input.reset();
    this.boardKey = key;
    this.cellCache = [];
    this.pointer = null;
    clearTimeout(this.resultTimer);
    const grid = this.$("#cell-grid");
    grid.style.gridTemplateColumns = `repeat(${state.puzzle.size},minmax(0,1fr))`;
    grid.style.gridTemplateRows = `repeat(${state.puzzle.size},minmax(0,1fr))`;
    grid.setAttribute(
      "aria-label",
      `${state.puzzle.size} by ${state.puzzle.size} puzzle. Arrow keys move, X marks, C places a cat.`,
    );
    grid.replaceChildren(
      ...state.puzzle.regions.map((_, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "cell";
        button.dataset.index = index;
        button.tabIndex = index === 0 ? 0 : -1;
        return button;
      }),
    );
    for (const token of this.tokens || [])
      this.portraits.unregister(token.element);
    this.tokens = Array.from({ length: state.puzzle.size }, (_, region) => {
      const element = document.createElement("span");
      element.className = "region-token";
      element.style.color = REGION_COLORS[region];
      element.innerHTML = icon("cat");
      return { element, region, found: false };
    });
    this.$("#region-progress").replaceChildren(
      ...this.tokens.map((token) => token.element),
    );
  }
  render(state) {
    this.state = state;
    const key = `${state.puzzle.id}:${state.variant}:${state.ordinal}:${state.mode}`;
    const reset = key !== this.boardKey || Boolean(state.effect.newPuzzle);
    if (reset)
      this.rebuildBoard(state, key);
    const { puzzle, cats, marks, failedCells, prefs } = state,
      blocked = prefs.autoMark ? blockedCells(puzzle, cats) : new Set();
    const cells = puzzle.regions.map((region, index) => ({
      region,
      color: REGION_COLORS[region],
      cat: cats.includes(index),
      mark: failedCells.includes(index) || marks.includes(index) || blocked.has(index),
      auto: !failedCells.includes(index) && !marks.includes(index) && blocked.has(index),
      failed: failedCells.includes(index),
      fixed: puzzle.givens.includes(index),
      label: prefs.regionLabels ? String.fromCharCode(65 + region) : "",
    }));
    const buttons = this.$("#cell-grid").children;
    cells.forEach((cell, index) => {
      const button = buttons[index],
        previous = this.cellCache[index] || {};
      if (previous.color !== cell.color)
        button.style.setProperty("--cell-color", cell.color);
      const description = cell.fixed
        ? "Fixed cat"
        : cell.cat
          ? "Cat"
          : cell.failed
            ? "Incorrect cat placement. Permanently ruled out"
            : cell.mark
              ? cell.auto
                ? "Automatically ruled out"
                : "Marked empty"
              : "Empty";
      if (previous.description !== description)
        setAttribute(
          button,
          "aria-label",
          `Row ${Math.floor(index / puzzle.size) + 1}, column ${(index % puzzle.size) + 1}, region ${String.fromCharCode(65 + cell.region)}. ${description}.`,
        );
      setAttribute(
        button,
        "aria-disabled",
        cell.fixed || cell.failed || state.status !== "playing" ||
          Boolean(state.tutorial && !state.tutorial.targets.includes(index)),
      );
      if (previous.cat !== cell.cat)
        button.classList.toggle("occupied", cell.cat);
      button.classList.toggle("failed", cell.failed);
      button.classList.toggle("mistake", state.effect.errorIndex === index);
      button.classList.toggle(
        "hinted",
        state.effect.hintIndex === index ||
          Boolean(state.effect.hintIndices?.includes(index)),
      );
      // Pixi draws its own cats. Fetch DOM cat images only for the fallback;
      // include the renderer in the cache key so a failed startup restores them.
      const fallback = this.renderer === this.fallbackRenderer;
      const signature = `${cell.cat}:${cell.mark}:${cell.auto}:${cell.failed}:${cell.label}:${fallback}`;
      if (previous.signature !== signature) {
        button.innerHTML = `<span class="cell-visual" aria-hidden="true">${cell.label ? `<span class="region-letter">${cell.label}</span>` : ""}${cell.cat ? (fallback ? `<img src="${this.assets.cat128}" srcset="${this.assets.cat128} 128w, ${this.assets.cat192} 192w, ${this.assets.cat} 256w" sizes="(min-width: 680px) 120px, calc(25vw - 10px)" width="256" height="256" alt="" draggable="false"/>` : "") : cell.mark ? brushCrossSvg(`cross-${index}`, cell.auto, cell.failed) : ""}</span>`;
      }
      this.cellCache[index] = { ...cell, description, signature };
    });
    this.renderer.render({
      key,
      size: puzzle.size,
      cells,
      revision: state.revision,
      effect: state.effect,
    });
    setText(this.$("#level-number"), state.ordinal);
    setText(this.$("#score-number"), state.score);
    const occupiedRegions = new Set(cats.map((index) => puzzle.regions[index]));
    for (const token of this.tokens) {
      const found = occupiedRegions.has(token.region);
      if (token.found === found) continue;
      token.found = found;
      token.element.classList.toggle("found", found);
      if (found) {
        token.element.innerHTML = `<img src="${this.assets.cat128}" width="128" height="128" alt=""/>`;
        this.portraits.register(token.element, token.region + 20, true);
      } else {
        this.portraits.unregister(token.element);
        token.element.innerHTML = icon("cat");
      }
    }
    setAttribute(
      this.$("#region-progress"),
      "aria-label",
      `${cats.length} of ${puzzle.size} cats found`,
    );
    this.lives.render(state.lives, state.effect.errorIndex !== undefined);
    for (const id of ["levels-button", "settings-button"])
      this.$(`#${id}`).disabled = Boolean(state.tutorial);
    const hintsLeft = state.monetizedHints ? state.crossHintBalance : Math.max(0, HINTS_PER_ROUND - state.hints);
    const watchForCat = state.monetizedHints && state.catHelpBalance === 0;
    setText(this.$("#hints-left"), hintsLeft);
    const badge = this.$("#cat-helps-left");
    if (watchForCat) {
      if (!badge.querySelector("svg")) badge.innerHTML = videoBadge;
    } else setText(badge, state.catHelpBalance);
    this.$("#cat-help-button").disabled =
      Boolean(state.tutorial) || state.status !== "playing" || (state.catHelpBalance === 0 && !watchForCat);
    setAttribute(
      this.$("#cat-help-button"),
      "aria-label",
      watchForCat ? "Watch a video to reveal one cat" : `Reveal one cat. ${state.catHelpBalance} helps remaining`,
    );
    setAttribute(this.$("#cat-help-button"), "title", watchForCat ? "Watch a video to reveal one cat" : "Reveal one cat");
    this.$("#hint-button").disabled =
      Boolean(state.tutorial) || state.status !== "playing" || hintsLeft === 0;
    setAttribute(
      this.$("#hint-button"),
      "aria-label",
      `Mark safe crosses. ${hintsLeft} hints remaining`,
    );
    setAttribute(this.$("#hint-button"), "title", state.monetizedHints && hintsLeft === 0 ? "Win a level to earn a cross hint" : "Mark a few safe crosses");
    this.$("#undo-button").disabled = Boolean(state.tutorial) || !state.canUndo;
    setText(
      this.$("#journey-progress"),
      `Level ${state.journeyLevel} · ${state.completedLevels} completed`,
    );
    setText(
      this.$("#new-puzzle"),
      state.journeyReady
        ? `Play level ${state.journeyLevel}`
        : "Continue journey",
    );
    setAttribute(
      this.$("#practice-tab"),
      "aria-pressed",
      state.mode === "practice",
    );
    setAttribute(this.$("#daily-tab"), "aria-pressed", state.mode === "daily");
    this.$("#auto-mark-setting").checked = prefs.autoMark;
    this.$("#drag-mark-setting").checked = prefs.dragMark;
    this.$("#sound-setting").checked = prefs.soundEffects;
    this.$("#region-label-setting").checked = prefs.regionLabels;
    this.updateSaveStatus();
    setText(
      this.$("#game-announcement"),
      state.effect.message || "",
    );
    if (state.effect.errorIndex !== undefined) {
      this.portraits.react("sad");
      clearTimeout(this.errorTimer);
      const index = state.effect.errorIndex;
      this.errorTimer = setTimeout(
        () => this.$("#cell-grid").children[index]?.classList.remove("mistake"),
        650,
      );
    } else if (state.effect.placementIndex !== undefined)
      this.portraits.react("happy");
    if (!state.tutorial && (state.effect.won || state.effect.lost)) {
      const currentKey = this.boardKey;
      this.resultTimer = setTimeout(() => {
        if (this.boardKey === currentKey) this.showResult();
      }, 900);
    }
    this.tutorialView.render(state.tutorial);
    this.feedback.render(state, reset);
    if (this.hostPaused) this.freezeHostAnimations();
  }
  updateSaveStatus() {
    setText(
      this.$("#save-status"),
      this.controller.canSave
        ? `Saved on this device${this.offlineReady ? " · Offline ready" : ""}`
        : "Saving is unavailable in this browser.",
    );
  }
  setOfflineReady() {
    this.offlineReady = true;
    this.updateSaveStatus();
  }
  requestHint() {
    this.input.reset();
    if (this.state.monetizedHints && this.state.crossHintBalance === 0) {
      setText(this.$("#game-announcement"), "Win a level to earn a cross hint.");
      return;
    }
    const effect = this.controller.perform("hint");
    if (
      !effect.changed &&
      this.state.status === "playing" &&
      (this.state.monetizedHints ? this.state.crossHintBalance > 0 : this.state.hints < HINTS_PER_ROUND)
    )
      setText(this.$("#game-announcement"), "All the empty spots are already marked. Look for the remaining cats.");
  }
  requestCatHelp() {
    this.input.reset();
    const state = this.controller.snapshot();
    if (state.catHelpBalance > 0) {
      this.controller.perform("revealCat");
      return;
    }
    if (!state.monetizedHints || state.tutorial || state.status !== "playing") return;
    if (!this.requestRewardedCatHint) {
      setText(this.$("#game-announcement"), "No video is available right now. Please try again later.");
      return;
    }
    const round = this.controller.active;
    let credited = false;
    this.runHostAction(() => {
      if (credited && this.controller.active === round && !this.controller.tutorial && round.data.status === "playing")
        this.controller.perform("revealCat");
      else if (!credited)
        setText(this.$("#game-announcement"), "No hint earned. Please try again when a video is available.");
    }, (context) => Promise.resolve(this.requestRewardedCatHint(context)).then((earned) => { credited = earned === true; }));
  }
  open(dialog) {
    if (this.hostPaused) {
      this.pausedDialogs = [dialog];
      return;
    }
    if (this.pendingHostAction) return;
    this.releasePointer();
    this.input.reset();
    this.controller.pause();
    for (const current of this.root.querySelectorAll("dialog[open]"))
      current.close();
    dialog.showModal();
    this.portraits.wake();
  }
  closeDialogs() {
    for (const dialog of this.root.querySelectorAll("dialog[open]"))
      dialog.close();
  }
  showResult() {
    const state = this.controller.snapshot();
    this.$("#result-title").textContent =
      state.status === "won" ? "Level complete!" : "Try again?";
    this.$("#result-stats").innerHTML =
      `<div><strong>${state.score}</strong><span>Score</span></div><div><strong>${time(state.elapsedMs)}</strong><span>Time</span></div><div><strong>${state.mistakes}</strong><span>Mistakes</span></div>`;
    this.$("#result-primary").textContent =
      state.status !== "won"
        ? "Try again"
        : state.mode === "daily"
          ? "Back to journey"
          : "Next level";
    this.open(this.$("#result-dialog"));
    this.feedback.inFrontOf(this.$("#result-dialog"));
    this.portraits.react(state.status === "won" ? "happy" : "sad");
  }
  cancelTap() {
    this.input.reset();
  }
  flushTap() {
    this.input.reset();
  }
  tap(index, event) {
    if (event.detail === 0) {
      this.renderer.setPressed(index, true);
      this.renderer.setPressed(index, false);
    }
    this.input.tap(index, { keyboard: event.detail === 0 });
  }
  releasePointer() {
    const pointer = this.pointer;
    this.pointer = null;
    this.stroke.end();
    if (!pointer) return;
    if (pointer.pressed !== null)
      this.renderer.setPressed(pointer.pressed, false);
    const grid = this.$("#cell-grid");
    if (grid.hasPointerCapture(pointer.id))
      grid.releasePointerCapture(pointer.id);
  }
  cancelPointer() {
    this.releasePointer();
    this.input.reset();
  }
  movePointer(event) {
    const pointer = this.pointer;
    if (!pointer || event.pointerId !== pointer.id) return;
    const point = { x: event.clientX, y: event.clientY };
    const index = tileAtPoint(pointer.geometry.tiles, point);
    if (
      !pointer.dragging &&
      ((index !== null && index !== pointer.index) ||
        Math.hypot(point.x - pointer.start.x, point.y - pointer.start.y) > 12)
    ) {
      pointer.dragging = true;
      this.input.reset();
    }
    if (pointer.dragging) {
      if (this.state.prefs.dragMark)
        this.stroke.paint([
          pointer.index,
          ...tilesAlongSegment(pointer.geometry.tiles, pointer.last, point),
        ]);
      if (index !== pointer.pressed) {
        if (pointer.pressed !== null)
          this.renderer.setPressed(pointer.pressed, false);
        pointer.pressed = index;
        if (index !== null) this.renderer.setPressed(index, true);
      }
    }
    pointer.last = point;
  }

  bind() {
    this.listen(document, "pointerdown", () => this.feedback.unlock());
    this.listen(document, "click", () => this.feedback.unlock());
    this.listen(document, "keydown", (event) => {
      if (!event.repeat) this.feedback.unlock();
    });
    const grid = this.$("#cell-grid");
    this.listen(grid, "pointerdown", (event) => {
      if (
        event.button !== 0 ||
        !event.isPrimary ||
        this.pointer ||
        this.state.status !== "playing"
      )
        return;
      const geometry = measureBoard(this.$("#board"));
      const point = { x: event.clientX, y: event.clientY };
      const index = tileAtPoint(geometry.tiles, point);
      if (index === null) return;
      // Handle pointer taps ourselves; the later native click must not repeat them.
      this.stroke.begin(index);
      this.pointer = {
        id: event.pointerId,
        index,
        pressed: index,
        start: point,
        last: point,
        geometry,
        dragging: false,
      };
      grid.setPointerCapture(event.pointerId);
      this.renderer.setPressed(index, true);
    });
    this.listen(grid, "pointermove", (event) => {
      if (event.pointerId !== this.pointer?.id) return;
      if (event.pointerType === "mouse" && !(event.buttons & 1)) {
        this.cancelPointer();
        return;
      }
      this.movePointer(event);
    });
    this.listen(document, "pointerup", (event) => {
      const pointer = this.pointer;
      if (!pointer || event.pointerId !== pointer.id) return;
      this.movePointer(event);
      const index = tileAtPoint(pointer.geometry.tiles, {
        x: event.clientX,
        y: event.clientY,
      });
      this.releasePointer();
      if (!pointer.dragging && index === pointer.index)
        this.input.tap(index);
    });
    this.listen(document, "pointercancel", (event) => {
      if (event.pointerId !== this.pointer?.id) return;
      this.cancelPointer();
    });
    this.listen(grid, "lostpointercapture", (event) => {
      if (event.pointerId !== this.pointer?.id) return;
      this.cancelPointer();
    });
    this.listen(window, "blur", () => this.cancelPointer());
    this.listen(window, "resize", () => this.cancelPointer());
    this.listen(window, "scroll", () => this.cancelPointer());
    this.listen(grid, "click", (event) => {
      if (event.detail !== 0) return;
      const cell = event.target.closest(".cell");
      if (cell) this.tap(Number(cell.dataset.index), event);
    });
    this.listen(grid, "contextmenu", (event) => event.preventDefault());
    this.listen(grid, "focusin", (event) => {
      const cell = event.target.closest(".cell");
      if (cell)
        for (const button of grid.children)
          button.tabIndex = button === cell ? 0 : -1;
    });
    this.listen(grid, "keydown", (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const cell = event.target.closest(".cell");
      if (!cell) return;
      const index = Number(cell.dataset.index),
        size = this.state.puzzle.size;
      const moves = {
        ArrowUp: index - size,
        ArrowDown: index + size,
        ArrowLeft: index - 1,
        ArrowRight: index + 1,
        Home: Math.floor(index / size) * size,
        End: Math.floor(index / size) * size + size - 1,
      };
      if (event.key in moves) {
        event.preventDefault();
        grid.children[
          Math.max(0, Math.min(size ** 2 - 1, moves[event.key]))
        ].focus();
        return;
      }
      const commands = {
          c: "place",
          x: "mark",
          Backspace: "clear",
          Delete: "clear",
        },
        command = commands[event.key] || commands[event.key.toLowerCase()];
      if (command) {
        event.preventDefault();
        this.cancelPointer();
        this.renderer.setPressed(index, true);
        this.renderer.setPressed(index, false);
        this.controller.perform(command, index);
      }
    });
    this.listen(document, "keydown", (event) => {
      if (
        this.state.tutorial ||
        document.querySelector("dialog[open]") ||
        event.target.matches("input,select,textarea") ||
        event.altKey
      )
        return;
      if (event.key.toLowerCase() === "z") {
        event.preventDefault();
        this.cancelPointer();
        this.controller.perform("undo");
      } else if (
        event.key.toLowerCase() === "h" &&
        !event.metaKey &&
        !event.ctrlKey
      ) {
        event.preventDefault();
        this.cancelPointer();
        this.requestHint();
      }
    });
    this.listen(this.$("#cat-help-button"), "click", () => {
      this.requestCatHelp();
    });
    this.listen(this.$("#hint-button"), "click", () => this.requestHint());
    this.listen(this.$("#undo-button"), "click", () => {
      this.input.reset();
      this.controller.perform("undo");
    });
    this.listen(this.$("#levels-button"), "click", () =>
      this.open(this.$("#levels-dialog")),
    );
    this.listen(this.$("#settings-button"), "click", () =>
      this.open(this.$("#settings-dialog")),
    );
    this.listen(this.$("#how-to-play"), "click", () =>
      this.open(this.$("#help-dialog")),
    );
    this.listen(this.$("#play-tutorial"), "click", () => {
      this.closeDialogs();
      this.controller.startTutorial();
    });
    this.listen(this.$("#new-puzzle"), "click", () => {
      this.input.reset();
      this.exitRound(() => this.controller.newPuzzle());
    });
    this.listen(this.$("#practice-tab"), "click", () => {
      this.exitRound(() => this.controller.selectMode("practice"), this.state.mode !== "practice");
    });
    this.listen(this.$("#daily-tab"), "click", () => {
      this.exitRound(() => this.controller.selectMode("daily"), this.state.mode !== "daily");
    });
    this.listen(this.$("#restart-button"), "click", () => {
      this.exitRound(() => this.controller.restart());
    });
    this.listen(this.$("#result-primary"), "click", () => {
      const won = this.state.status === "won";
      this.exitRound(() => won ? this.controller.newPuzzle() : this.controller.restart());
    });
    this.listen(this.$("#auto-mark-setting"), "change", (event) =>
      this.controller.setPreference("autoMark", event.target.checked),
    );
    this.listen(this.$("#drag-mark-setting"), "change", (event) => {
      this.cancelPointer();
      this.controller.setPreference("dragMark", event.target.checked);
    });
    this.listen(this.$("#sound-setting"), "change", (event) =>
      this.controller.setPreference("soundEffects", event.target.checked),
    );
    this.listen(this.$("#region-label-setting"), "change", (event) =>
      this.controller.setPreference("regionLabels", event.target.checked),
    );
    for (const dialog of this.root.querySelectorAll("dialog")) {
      for (const button of dialog.querySelectorAll("[data-close]"))
        this.listen(button, "click", () => dialog.close());
      this.listen(dialog, "close", () => {
        if (dialog.id === "result-dialog") this.feedback.stopCelebration();
        this.resumeIfReady();
        this.portraits.wake();
      });
      this.listen(dialog, "click", (event) => {
        const box = dialog.getBoundingClientRect();
        if (
          event.target === dialog &&
          (event.clientX < box.left ||
            event.clientX > box.right ||
            event.clientY < box.top ||
            event.clientY > box.bottom)
        )
          dialog.close();
      });
    }
    this.listen(document, "visibilitychange", () => {
      if (document.hidden) {
        this.feedback.suspend();
        this.releasePointer();
        this.controller.pause();
      } else {
        this.completeHostAction();
        this.resumeIfReady();
      }
    });
    this.listen(window, "pagehide", () => {
      this.feedback.suspend();
      this.releasePointer();
      this.input.reset();
      this.controller.pause();
    });
  }
  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.pendingHostAction = null;
    this.releaseHostAnimations();
    for (const element of this.root.querySelectorAll("#game, dialog, #tutorial-overlay")) element.inert = false;
    this.$("#game").removeAttribute("aria-busy");
    this.releasePointer();
    this.abort.abort();
    this.unsubscribe?.();
    this.input.reset();
    clearTimeout(this.resultTimer);
    clearTimeout(this.errorTimer);
    clearInterval(this.saveInterval);
    this.lives.destroy();
    this.tutorialView.destroy();
    this.portraits.destroy();
    this.feedback.destroy();
    this.renderer.destroy();
  }
}
